// eslint-disable-next-line no-unused-vars
import React, { memo, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { isMobile } from 'react-device-detect';
import { useInView } from 'react-intersection-observer';
import { Global, css } from '@emotion/react';

import useUiState from 'Hooks/useUiState';
import useUser from 'Hooks/useUser';
import useScreen from 'Hooks/useScreen';

import Search from 'Containers/Search/Search';
// import SettingsMenu from 'Containers/Menus/Settings';
import UserMenu from 'Containers/Menus/User';
import SettingsBar from 'Containers/SettingsBar/SettingsBar';
import Breadcrumbs from 'Containers/Breadcrumbs/Breadcrumbs';
import Alerts from 'Containers/Alerts/Alerts';
import CartAndWishlists from 'Containers/CartAndWishlists/CartAndWishlists';
import AlertsModal from 'Containers/Alerts/AlertsModal';

import HeaderComponent from 'Components/Header/Header';
import TopBar from 'Components/Header/TopBar';
// import Navigation from 'Components/Navigation/Navigation';
import CrossSalesPopup from 'Containers/Products/CrossSalesPopup';

import reportWebVitals from 'Utils/reportWebVitals';
import ChatBotWidget from 'Containers/ChatbotWidget/ChatbotWidget';

reportWebVitals(console.log);

const Header = memo(() => {
  const { ref, inView, entry } = useInView({ threshold: 0, initialInView: true });
  const { uiState, setUiState } = useUiState();
  const { data: user } = useUser();
  const screen = useScreen();

  const rootHeader = useRef(null);
  const canHeaderFloat = (isMobile || !inView);

  const handleOverlayClick = useCallback(
    () => {
      setUiState({ searchOpen: false });
    },
    []
  );

  useEffect(() => {
    if (
      !rootHeader.current
      && typeof document !== 'undefined'
    ) {
      rootHeader.current = document.getElementById('root-header');
    }
  }, [rootHeader.current]);

  useEffect(() => {
    // todo: enhance for when root component gets unmounted
    if (rootHeader.current) {
      if (!inView) {
        if (entry?.target) {
          rootHeader.current.classList.add('sticky');
        }
      } else {
        rootHeader.current.classList.remove('sticky');
      }
    }
  }, [inView]);

  const headerContents = useMemo(
    () => (
      <>
        <Global
          styles={
            css`
              .Header {
                top: 0;
                z-index: 9000;
                position: -webkit-sticky;
                position: sticky;
                transform: translateY(0);
                transition: transform 0.1s ease;

                &.sticky {
                  transform: translateY(-58px);
                }
              }

              @media screen and (max-width: 1024px) {
                .Header.sticky {
                  transform: translateY(-38px)
                }
              }

              @media screen and (max-width: 767px) {
                .Header.sticky {
                  transform: none;
                }
              }
            `
          }
        />
        <HeaderComponent
          canFloat={ canHeaderFloat }
          onOverlayClick={ handleOverlayClick }
          searchOpen={ uiState?.searchOpen }
        >
          <TopBar canFloat={ canHeaderFloat }>
            { user?.email && (
              <>
                { screen.width > 767 && <Search /> }
                <div className="right">
                  <UserMenu canFloat={ canHeaderFloat } />
                  {/* <SettingsMenu /> */}
                  <Alerts />
                  <CartAndWishlists canFloat={ canHeaderFloat } />
                </div>
                { screen.width <= 767 && <Search /> }
              </>
            ) }
          </TopBar>
        </HeaderComponent>
      </>
    ),
    [canHeaderFloat, uiState?.searchOpen, screen]
  );

  if (rootHeader.current) {
    return (
      <>
        {
          createPortal(
            <>
              <SettingsBar />
              { headerContents }
            </>,
            rootHeader.current
          )
        }
        { user?.email && (
          <>
            {/* <Navigation /> */}
            <Breadcrumbs />
            <CrossSalesPopup />
            <ChatBotWidget />
          </>
        ) }
        <AlertsModal />
        <div id="headerIntersectionObserverHook" ref={ ref } />
      </>
    );
  }

  return (
    <>
      <header className={ `Header ${ canHeaderFloat ? 'sticky' : '' }` }>
        <SettingsBar />
        { headerContents }
      </header>
      { user?.email && (
        <>
          {/* <Navigation /> */}
          <Breadcrumbs />
          <CrossSalesPopup />
          <ChatBotWidget />
        </>
      ) }
      <AlertsModal />
      <div id="headerIntersectionObserverHook" ref={ ref } />
    </>
  );
});

Header.displayName = 'HeaderContainer';

export default Header;
